import { createRoute, Link, redirect } from '@tanstack/react-router'
import { rootRoute } from './__root'
import { WorkspaceHeader } from '@/components/WorkspaceHeader'
import { apiClient } from '@/lib/api'
import { usePlatform } from '@/hooks/usePlatform'
import { usePluginEntitlementMap } from '@/hooks/usePluginEntitlement'

/**
 * Werkshop Tools picker — lists the registered plugins that ship
 * a full-screen editor (Sampler, Piano Roll, Automation). Each
 * tile routes into `/werkshop/tools/$pluginId` where the plugin
 * mounts its own editor surface — UNLESS the user hasn't licensed
 * the module, in which case the tile routes to
 * `/werkshop/store#<moduleId>` so they can purchase it. Keeping
 * the lock affordance in the picker mirrors the pad-editor and
 * store card behaviour, so users never get dropped into an editor
 * they can't actually use.
 */
export const werkshopToolsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/werkshop/tools',
  beforeLoad: async () => {
    try {
      await apiClient.get('/api/v1/auth/me')
    } catch {
      throw redirect({ to: '/login' })
    }
  },
  component: ToolsPicker,
})

// Sampler intentionally omitted: in the Sequencer the Plugin tab
// embeds the Slicer directly when a sample pad is focused (PR #227),
// and having two entry points (a standalone /werkshop/tools/sampler
// page AND the in-sequencer tab) confused users. The route itself
// stays alive for deep-link bookmarks, just not advertised here.
const TOOLS: ReadonlyArray<{
  to: '/werkshop/tools/piano-roll' | '/werkshop/tools/automation'
  moduleId: string
  label: string
  description: string
}> = [
  {
    to: '/werkshop/tools/piano-roll',
    moduleId: 'piano-roll',
    label: 'Piano Roll',
    description: 'Per-pad notes at sub-step resolution',
  },
  {
    to: '/werkshop/tools/automation',
    moduleId: 'automation',
    label: 'Automation',
    description: 'Parameter envelopes on pads, buses, and project',
  },
]

function ToolsPicker() {
  const platform = usePlatform()
  const entitlementFor = usePluginEntitlementMap()
  return (
    <div className="min-h-[100dvh] bg-surface-base">
      <WorkspaceHeader title="Tools" workspaceSwitcher="werkshop" />
      <main
        className={
          platform.isNative
            ? 'w-full px-3 sm:px-4 py-4 flex flex-col gap-4'
            : 'max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8 flex flex-col gap-4'
        }
      >
        <p className="text-sm text-content-secondary">
          Full-screen plugin editors. Each tool opens bound to your
          currently-open project; changes save back into the same session.
        </p>
        <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
          {TOOLS.map(tool => {
            const entitlement = entitlementFor(tool.moduleId)
            const locked = entitlement === null
            if (locked) {
              return (
                <Link
                  key={tool.to}
                  to="/werkshop/store"
                  hash={tool.moduleId}
                  className="group rounded-xl border border-brd-subtle bg-surface-raised p-4 hover:border-accent transition-colors flex flex-col gap-1"
                  title={`${tool.label} requires a Personal or Commercial license`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-semibold text-content-secondary group-hover:text-accent">
                      {tool.label}
                    </div>
                    <span
                      aria-label="Locked — visit Store to purchase"
                      className="text-[10px] font-mono uppercase tracking-wider text-accent border border-accent/40 rounded px-1.5 py-0.5"
                    >
                      🔒 Store
                    </span>
                  </div>
                  <div className="text-xs text-content-tertiary">
                    {tool.description}
                  </div>
                </Link>
              )
            }
            return (
              <Link
                key={tool.to}
                to={tool.to}
                className="group rounded-xl border border-brd-subtle bg-surface-raised p-4 hover:border-accent transition-colors flex flex-col gap-1"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold text-content-primary group-hover:text-accent">
                    {tool.label}
                  </div>
                  {entitlement === 'commercial' && (
                    <span className="text-[10px] font-mono uppercase tracking-wider text-accent">
                      commercial
                    </span>
                  )}
                </div>
                <div className="text-xs text-content-secondary">
                  {tool.description}
                </div>
              </Link>
            )
          })}
        </div>
      </main>
    </div>
  )
}
